import { useEffect, useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'

import { useAuth } from '@/lib/auth'
import { cx } from '@/components/ui'

const NAV = [
  { to: '/', label: 'Dashboard', icon: '◱', end: true },
  { to: '/opportunities', label: 'Opportunities', icon: '◈' },
  { to: '/introductions', label: 'Introductions', icon: '⇄' },
  { to: '/contacts', label: 'Contacts', icon: '☰' },
  { to: '/conversations', label: 'Conversations', icon: '❝' },
  { to: '/network', label: 'Network', icon: '⁂' },
  { to: '/logs', label: 'Logs', icon: '≡' },
  { to: '/danger', label: 'Danger zone', icon: '⚠', ownerOnly: true },
]

type Theme = 'light' | 'dark' | 'system'

export function Layout() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [theme, setTheme] = useState<Theme>(
    () => (localStorage.getItem('leadconnector.theme') as Theme) || 'system',
  )
  const [navOpen, setNavOpen] = useState(false)

  useEffect(() => {
    const root = document.documentElement
    if (theme === 'system') root.removeAttribute('data-theme')
    else root.setAttribute('data-theme', theme)
    localStorage.setItem('leadconnector.theme', theme)
  }, [theme])

  return (
    <div className="flex min-h-screen">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded focus:bg-surface focus:px-3 focus:py-2"
      >
        Skip to content
      </a>

      <aside
        className={cx(
          'fixed inset-y-0 left-0 z-40 flex w-60 flex-col border-r border-line bg-surface transition-transform lg:static lg:translate-x-0',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex items-center gap-2.5 px-4 py-4">
          <span
            aria-hidden="true"
            className="flex h-7 w-7 items-center justify-center rounded-md text-sm font-bold text-white"
            style={{ background: 'var(--series-1)' }}
          >
            K
          </span>
          <div className="leading-tight">
            <p className="text-sm font-semibold text-ink">LeadConnector</p>
            <p className="text-xs text-ink-faint">Opportunity matching</p>
          </div>
        </div>

        <nav className="flex-1 space-y-0.5 px-2 py-2">
          {NAV.filter((item) => !item.ownerOnly || user?.role === 'owner').map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setNavOpen(false)}
              className={({ isActive }) => cx('nav-link', isActive && 'nav-link-active')}
            >
              <span aria-hidden="true" className="w-4 text-center text-ink-faint">
                {item.icon}
              </span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-line px-3 py-3">
          <div className="mb-2 flex items-center gap-1 rounded-md p-0.5" style={{ background: 'var(--surface-2)' }}>
            {(['light', 'system', 'dark'] as Theme[]).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setTheme(option)}
                aria-pressed={theme === option}
                className={cx(
                  'flex-1 rounded px-2 py-1 text-xs capitalize transition-colors',
                  theme === option ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted',
                )}
              >
                {option}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-2 px-1">
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-ink">{user?.full_name || user?.email}</p>
              <p className="text-xs capitalize text-ink-faint">{user?.role}</p>
            </div>
            <button
              type="button"
              className="btn-ghost px-2 py-1 text-xs"
              onClick={() => {
                signOut()
                navigate('/login')
              }}
            >
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {navOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setNavOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center gap-3 border-b border-line bg-surface px-4 py-2.5 lg:hidden">
          <button
            type="button"
            className="btn-ghost px-2 py-1"
            onClick={() => setNavOpen(true)}
            aria-label="Open navigation"
          >
            ☰
          </button>
          <span className="text-sm font-semibold text-ink">LeadConnector</span>
        </header>

        <main id="main" className="min-w-0 flex-1 px-4 py-6 lg:px-8 lg:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
